import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  MessageCircle,
  Building,
  KeyRound,
  ShieldCheck,
  ClipboardCheck,
  Handshake,
  Send,
  Lock,
  Eye,
  Scale,
  Sparkles,
  AlertCircle,
  Check,
  ArrowLeft,
} from "lucide-react";
import { NEXO, nexoWa } from "../lib/nexo";
import CookieConsent from "../components/CookieConsent";

const OFFICE_IMG =
  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=2000&q=80";
const CONSULTANT_IMG =
  "https://images.unsplash.com/photo-1573497491765-dccce02b29df?auto=format&fit=crop&w=1600&q=80";

// ------------------ small helpers ------------------
const H2 = ({ children, id, testid, small = false }) => (
  <h2
    id={id}
    data-testid={testid}
    className={`font-serif-ag text-[#0D1B2A] font-light leading-[1.05] tracking-[-0.015em] ${
      small
        ? "text-3xl md:text-4xl lg:text-[2.6rem]"
        : "text-4xl md:text-5xl lg:text-[3.4rem]"
    }`}
  >
    {children}
  </h2>
);

const Overline = ({ children, dark = false }) => (
  <div className="inline-flex items-center gap-3 mb-6">
    <span
      className={`w-8 h-px ${dark ? "bg-[#C8A25B]/70" : "bg-[#C8A25B]"}`}
    />
    <span
      className={`text-[10px] tracking-[0.32em] uppercase font-medium ${
        dark ? "text-[#C8A25B]" : "text-[#7A6033]"
      }`}
    >
      {children}
    </span>
  </div>
);

// ------------------ HERO ------------------
function Hero() {
  return (
    <section
      data-testid="nexo-hero"
      className="relative overflow-hidden bg-[#0D1B2A]"
    >
      <div className="absolute inset-0">
        <img
          src={OFFICE_IMG}
          alt=""
          className="w-full h-full object-cover opacity-25"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(115deg, rgba(13,27,42,0.94) 0%, rgba(13,27,42,0.7) 55%, rgba(13,27,42,0.35) 100%)",
          }}
        />
        <div
          className="absolute -top-40 -right-40 w-[620px] h-[620px] rounded-full opacity-30"
          style={{
            background:
              "radial-gradient(circle, rgba(200,162,91,0.4) 0%, transparent 65%)",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-40 md:pt-44 pb-24 min-h-[95vh] flex flex-col justify-center">
        {/* NEXO badge */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-4 pl-4 pr-6 py-2.5 border border-[#C8A25B]/50 bg-[#C8A25B]/[0.06] backdrop-blur-sm">
            <span
              className="font-serif-ag text-[#C8A25B] text-lg tracking-[0.32em] font-semibold"
              style={{ letterSpacing: "0.42em" }}
            >
              NEXO
            </span>
            <span className="w-px h-4 bg-[#C8A25B]/40" />
            <span className="text-[10px] tracking-[0.28em] uppercase text-white/80">
              Operação de Crédito & Patrimônio
            </span>
          </div>
        </div>

        <h1
          data-testid="nexo-hero-headline"
          className="font-serif-ag text-white text-[2.6rem] md:text-6xl lg:text-[4.5rem] xl:text-[5rem] font-light leading-[1.02] tracking-[-0.02em] max-w-5xl"
        >
          Sua conquista merece sair do papel.{" "}
          <span className="text-[#C8A25B] italic">
            Nós cuidamos da garantia que falta.
          </span>
        </h1>

        <p className="mt-8 max-w-2xl text-lg md:text-xl text-white/70 font-light leading-relaxed">
          Você já tem o crédito em mãos. Agora, deixe a NEXO analisar a
          alternativa de garantia imobiliária para a sua operação fluir — com o
          cuidado e a discrição de um private bank.
        </p>

        <div className="mt-12 flex flex-col sm:flex-row gap-4">
          <a
            href={nexoWa("Olá NEXO, quero conversar sobre a minha carta contemplada.")}
            target="_blank"
            rel="noreferrer"
            data-testid="nexo-hero-cta"
            className="group inline-flex items-center justify-center gap-3 bg-[#C8A25B] text-[#0D1B2A] px-8 py-4 text-xs md:text-sm tracking-[0.22em] uppercase font-semibold hover:bg-[#D9B672] transition-all duration-300"
          >
            <MessageCircle size={17} strokeWidth={1.6} />
            Quero conversar sobre minha carta
            <ArrowUpRight
              size={16}
              strokeWidth={1.6}
              className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
            />
          </a>
          <a
            href="#formulario"
            data-testid="nexo-hero-cta-form"
            className="inline-flex items-center justify-center gap-2 border border-white/25 text-white/90 px-8 py-4 text-xs md:text-sm tracking-[0.22em] uppercase font-light hover:border-[#C8A25B] hover:text-[#C8A25B] transition-all"
          >
            Solicitar análise
          </a>
        </div>

        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-3xl">
          {[
            ["Confidencialidade", "Tratativa sob sigilo"],
            ["Análise sob medida", "Cada operação é única"],
            ["Rapidez cirúrgica", "Sem burocracia"],
            ["Suporte próximo", "Do início ao fim"],
          ].map(([k, v], i) => (
            <div key={i} className="border-l border-[#C8A25B]/40 pl-4">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#C8A25B]">
                {k}
              </div>
              <div className="text-xs text-white/70 mt-1 font-light">{v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ------------------ A DOR ------------------
function Pain() {
  const bullets = [
    "Você tem uma carta contemplada em mãos, mas a administradora exige garantia imobiliária.",
    "Não possui um imóvel livre em seu nome — ou não quer comprometer o seu patrimônio.",
    "Cada dia parado é uma oportunidade que passa: a obra que não começa, o negócio que espera.",
    "Você já fez a parte difícil. Falta o último elo para o crédito virar realidade.",
  ];

  return (
    <section
      data-testid="nexo-pain"
      id="a-dor"
      className="relative bg-white py-24 md:py-32"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-6">
          <Overline>Entendemos você</Overline>
          <H2 testid="nexo-pain-headline">
            O que está impedindo você de{" "}
            <span className="italic text-[#C8A25B]">usar seu crédito hoje?</span>
          </H2>
          <p className="mt-6 text-[#43536A] text-base md:text-lg font-light leading-relaxed max-w-xl">
            Ter a carta contemplada e não conseguir avançar é uma das
            frustrações mais silenciosas do mercado. A NEXO nasceu justamente
            para esse ponto — para que a distância entre <em>“já tenho o
            crédito”</em> e <em>“já estou usando o crédito”</em> seja o menor
            possível.
          </p>
        </div>

        <ul className="lg:col-span-6 space-y-5">
          {bullets.map((b, i) => (
            <li
              key={i}
              data-testid={`nexo-pain-${i}`}
              className="flex items-start gap-4 border-b border-[#0D1B2A]/8 pb-5"
            >
              <AlertCircle
                size={20}
                strokeWidth={1.4}
                className="text-[#C8A25B] mt-1 shrink-0"
              />
              <span className="text-[#0D1B2A] text-base md:text-lg font-light leading-relaxed">
                {b}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ------------------ MENSAGEM HUMANIZADA ------------------
function Message() {
  return (
    <section
      data-testid="nexo-message"
      id="mensagem"
      className="relative bg-[#0D1B2A] py-24 md:py-32 overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(200,162,91,0.25), transparent 55%)",
        }}
      />
      {/* subtle gold hairlines */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-px bg-gradient-to-r from-transparent via-[#C8A25B]/60 to-transparent" />

      <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
        <div className="flex items-center justify-center gap-3 mb-8">
          <span className="w-8 h-px bg-[#C8A25B]/70" />
          <span className="text-[10px] tracking-[0.32em] uppercase text-[#C8A25B] font-medium">
            Uma palavra para você
          </span>
          <span className="w-8 h-px bg-[#C8A25B]/70" />
        </div>

        <h2
          data-testid="nexo-message-headline"
          className="font-serif-ag text-white text-3xl md:text-4xl lg:text-[3rem] font-light leading-[1.1] tracking-[-0.015em]"
        >
          Sua carta foi contemplada, mas a garantia exigida{" "}
          <span className="italic text-[#C8A25B]">travou o seu sonho</span> da
          casa própria?
        </h2>

        <div className="mt-12 space-y-7 text-left md:text-center max-w-3xl mx-auto">
          <p className="text-white/85 text-base md:text-lg lg:text-[1.15rem] font-light leading-[1.75]">
            É <span className="text-[#C8A25B] italic">frustrante</span> ter o
            crédito aprovado e esbarrar na burocracia na hora de apresentar
            garantias complementares. Sabemos o quanto você esperou por esse
            momento — e como a falta de opções flexíveis no mercado pode gerar{" "}
            <span className="text-white">ansiedade</span>.
          </p>

          <p className="text-white/85 text-base md:text-lg lg:text-[1.15rem] font-light leading-[1.75]">
            A{" "}
            <span
              className="font-serif-ag text-[#C8A25B]"
              style={{ letterSpacing: "0.18em", fontWeight: 600 }}
            >
              NEXO
            </span>{" "}
            chegou para <span className="italic text-[#C8A25B]">simplificar</span>{" "}
            a sua jornada. Oferecemos{" "}
            <span className="text-white">suporte personalizado</span> e{" "}
            <span className="text-white">soluções práticas</span> para que você
            complete a garantia do seu imóvel de forma{" "}
            <span className="text-[#C8A25B]">rápida e segura</span>, sem
            complicações desnecessárias.
          </p>

          <p className="text-white/85 text-base md:text-lg lg:text-[1.15rem] font-light leading-[1.75]">
            Não deixe que as exigências do mercado parem o seu planejamento.
            Fale com a nossa equipe{" "}
            <span className="text-white italic">hoje mesmo</span> e descubra
            como <span className="text-[#C8A25B] italic">destravar</span> a sua
            carta de crédito com quem entende do assunto.
          </p>
        </div>

        <div className="mt-14 flex items-center justify-center">
          <a
            href="#formulario"
            data-testid="nexo-message-cta"
            className="group inline-flex items-center gap-3 border border-[#C8A25B]/70 hover:bg-[#C8A25B] hover:text-[#0D1B2A] text-[#C8A25B] px-9 py-4 text-xs md:text-sm tracking-[0.28em] uppercase font-medium transition-all duration-300"
          >
            Clique e dê o próximo passo
            <ArrowUpRight
              size={16}
              strokeWidth={1.6}
              className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
            />
          </a>
        </div>
      </div>

      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-40 h-px bg-gradient-to-r from-transparent via-[#C8A25B]/60 to-transparent" />
    </section>
  );
}

// ------------------ SOLUÇÃO NEXO ------------------
function Solution() {
  const flow = [
    { icon: KeyRound, label: "Carta Contemplada" },
    { icon: Building, label: "Garantia Imobiliária NEXO" },
    { icon: ClipboardCheck, label: "Análise Individual" },
    { icon: Handshake, label: "Formalização" },
    { icon: Send, label: "Entrega à Administradora" },
  ];

  return (
    <section
      data-testid="nexo-solution"
      id="solucao"
      className="relative bg-[#F5F1EA] py-24 md:py-32"
    >
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(200,162,91,0.15), transparent 45%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#C8A25B]" />
            <span className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#7A6033]">
              A solução NEXO
            </span>
            <span className="w-8 h-px bg-[#C8A25B]" />
          </div>
          <H2>
            Um fluxo <span className="italic text-[#C8A25B]">simples</span> para
            um passo que parecia impossível.
          </H2>
        </div>

        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 md:gap-2">
          {flow.map((f, i) => (
            <React.Fragment key={i}>
              <div
                data-testid={`nexo-flow-${i}`}
                className="group flex-1 bg-white border border-[#0D1B2A]/10 hover:border-[#C8A25B]/60 p-6 md:p-7 flex flex-col items-center text-center transition-all duration-500"
              >
                <div className="w-12 h-12 border border-[#C8A25B]/40 flex items-center justify-center mb-4 group-hover:bg-[#C8A25B]/10 transition-colors">
                  <f.icon
                    size={22}
                    strokeWidth={1.3}
                    className="text-[#C8A25B]"
                  />
                </div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#7A6033] mb-2">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-serif-ag text-[#0D1B2A] text-lg md:text-xl leading-tight">
                  {f.label}
                </div>
              </div>
              {i < flow.length - 1 && (
                <div className="hidden md:block text-[#C8A25B] text-2xl">
                  →
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

// ------------------ COMO CAMINHAMOS JUNTOS ------------------
function Steps() {
  const steps = [
    {
      t: "Conversa inicial, sem compromisso",
      d: "Recebemos seus dados e conversamos com calma para entender o momento, o valor da carta e a administradora envolvida.",
    },
    {
      t: "Análise sob medida do seu caso",
      d: "Nosso time avalia a viabilidade da garantia imobiliária NEXO e desenha a alternativa que faz sentido para a sua operação.",
    },
    {
      t: "Apresentação transparente da proposta",
      d: "Você recebe uma proposta clara — condições, prazos e custos — sem cláusulas escondidas nem letra miúda.",
    },
    {
      t: "Formalização com respaldo jurídico",
      d: "Cuidamos de toda a documentação e da formalização em cartório, com o mesmo rigor de uma operação institucional.",
    },
    {
      t: "Entrega à administradora e liberação",
      d: "Fechamos o elo: a garantia é apresentada, a administradora libera o crédito e o seu plano finalmente sai do papel.",
    },
  ];

  return (
    <section
      data-testid="nexo-steps"
      id="caminho"
      className="relative bg-[#0D1B2A] py-24 md:py-32 overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 80% 20%, rgba(200,162,91,0.25), transparent 55%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-20">
          <Overline dark>Como caminhamos juntos</Overline>
          <h2 className="font-serif-ag text-white text-4xl md:text-5xl lg:text-[3.4rem] font-light leading-[1.05] tracking-[-0.015em]">
            Do <span className="italic text-[#C8A25B]">primeiro contato</span>{" "}
            à liberação do crédito.
          </h2>
          <p className="mt-6 text-white/70 text-base md:text-lg font-light leading-relaxed max-w-2xl">
            Cada etapa é conduzida por um consultor sênior. Você nunca fica
            perdido — nem um passo é dado sem que você entenda exatamente o que
            está acontecendo.
          </p>
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
          {steps.map((s, i) => (
            <li
              key={i}
              data-testid={`nexo-step-${i}`}
              className="relative pl-16 md:pl-20"
            >
              <span className="absolute left-0 top-0 font-serif-ag text-[#C8A25B] text-4xl md:text-5xl font-light leading-none tracking-tight">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-serif-ag text-white text-xl md:text-2xl font-light leading-tight">
                {s.t}
              </h3>
              <p className="mt-3 text-white/60 text-base font-light leading-relaxed">
                {s.d}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ------------------ PARA QUEM É ------------------
function ForWhom() {
  const items = [
    {
      icon: KeyRound,
      t: "Você tem carta contemplada",
      d: "Ganhou por sorteio, lance ou adquiriu — e agora precisa da garantia para a administradora liberar.",
    },
    {
      icon: Building,
      t: "Não tem imóvel próprio para dar em garantia",
      d: "Ou prefere não comprometer o patrimônio pessoal / familiar em uma operação de consórcio.",
    },
    {
      icon: Sparkles,
      t: "Quer avançar com rapidez",
      d: "Sabe que cada dia importa e busca uma alternativa profissional para destravar a operação.",
    },
  ];

  return (
    <section
      data-testid="nexo-for-whom"
      id="para-quem"
      className="relative bg-white py-24 md:py-32"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <Overline>Para quem é a NEXO</Overline>
          <H2 small>
            Se você se reconhecer em{" "}
            <span className="italic text-[#C8A25B]">qualquer um</span> destes
            perfis, precisamos conversar.
          </H2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <div
              key={i}
              data-testid={`nexo-forwhom-${i}`}
              className="group bg-[#F5F1EA] border border-[#0D1B2A]/8 hover:border-[#C8A25B]/50 p-8 md:p-10 transition-colors duration-500"
            >
              <it.icon
                size={26}
                strokeWidth={1.2}
                className="text-[#C8A25B] mb-6"
              />
              <h3 className="font-serif-ag text-[#0D1B2A] text-xl md:text-2xl font-medium leading-snug">
                {it.t}
              </h3>
              <p className="mt-3 text-[#43536A] text-sm md:text-base font-light leading-relaxed">
                {it.d}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ------------------ CREDIBILIDADE ------------------
function Credibility() {
  const pillars = [
    {
      icon: Lock,
      t: "Confidencialidade",
      d: "Sua operação é tratada com discrição absoluta, do primeiro contato à assinatura final.",
    },
    {
      icon: Eye,
      t: "Transparência",
      d: "Nenhuma cláusula escondida. Cada número, prazo e taxa é explicado antes da sua decisão.",
    },
    {
      icon: Scale,
      t: "Análise sob medida",
      d: "Nada é padronizado. Cada caso é estudado individualmente por um consultor sênior.",
    },
  ];

  return (
    <section
      data-testid="nexo-credibility"
      className="relative bg-[#F5F1EA] py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -top-4 -left-4 right-6 bottom-6 border border-[#C8A25B]/50" />
            <img
              src={CONSULTANT_IMG}
              alt="Consultor sênior NEXO em atendimento"
              className="relative w-full aspect-[4/5] object-cover grayscale-[10%]"
              data-testid="nexo-credibility-image"
            />
          </div>
        </div>
        <div className="lg:col-span-7 lg:pl-6">
          <Overline>Credibilidade</Overline>
          <H2>
            Um relacionamento à altura do{" "}
            <span className="italic text-[#C8A25B]">seu patrimônio</span>.
          </H2>
          <p className="mt-6 text-[#43536A] text-base md:text-lg font-light leading-relaxed max-w-xl">
            Conduzimos cada operação com o mesmo padrão do private banking — com
            a diferença que aqui você fala diretamente com quem decide, sem
            filas nem intermediários.
          </p>

          <div className="mt-10 space-y-6">
            {pillars.map((p, i) => (
              <div
                key={i}
                data-testid={`nexo-pillar-${i}`}
                className="flex items-start gap-5 border-b border-[#0D1B2A]/10 pb-5 last:border-0"
              >
                <div className="w-11 h-11 border border-[#C8A25B]/60 flex items-center justify-center shrink-0">
                  <p.icon size={18} strokeWidth={1.4} className="text-[#C8A25B]" />
                </div>
                <div>
                  <h4 className="font-serif-ag text-[#0D1B2A] text-xl leading-tight">
                    {p.t}
                  </h4>
                  <p className="text-[#43536A] text-sm md:text-base mt-1 font-light leading-relaxed">
                    {p.d}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ------------------ FORM ------------------
const ADMINS = [
  "Porto Seguro",
  "Bradesco",
  "Itaú",
  "Santander",
  "HS Consórcios",
  "Randon",
  "Volkswagen",
  "Outra",
];

const brl = (n) => {
  if (n === "" || n === null || n === undefined) return "";
  return Number(n).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
};
const parseInt2 = (s) => {
  const n = String(s).replace(/[^\d]/g, "");
  return n ? parseInt(n, 10) : 0;
};

function FormSection() {
  const [nome, setNome] = useState("");
  const [wa, setWa] = useState("");
  const [valor, setValor] = useState(0);
  const [valorStr, setValorStr] = useState("");
  const [admin, setAdmin] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleValor = (e) => {
    const raw = parseInt2(e.target.value);
    setValor(raw);
    setValorStr(raw ? brl(raw) : "");
  };

  const handleWa = (e) => {
    // mask (DD) 9XXXX-XXXX
    let v = e.target.value.replace(/\D/g, "").slice(0, 11);
    if (v.length > 6) v = `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
    else if (v.length > 2) v = `(${v.slice(0, 2)}) ${v.slice(2)}`;
    else if (v.length > 0) v = `(${v}`;
    setWa(v);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!nome.trim() || !wa.trim() || !valor || !admin) {
      setError("Por favor, preencha todos os campos para continuarmos.");
      return;
    }
    setError("");
    setSent(true);
    // trigger WhatsApp with pre-filled details
    const msg = `Olá NEXO! Meu nome é ${nome}.\nWhatsApp: ${wa}\nValor da carta: ${brl(
      valor
    )}\nAdministradora: ${admin}\n\nGostaria que vocês analisassem minha operação.`;
    setTimeout(() => {
      window.open(nexoWa(msg), "_blank", "noopener,noreferrer");
    }, 400);
  };

  return (
    <section
      id="formulario"
      data-testid="nexo-form-section"
      className="relative bg-[#0D1B2A] py-24 md:py-32 overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 20% 80%, rgba(200,162,91,0.28), transparent 55%)",
        }}
      />

      <div className="relative max-w-4xl mx-auto px-6 md:px-12">
        {!sent ? (
          <>
            <div className="text-center mb-12">
              <Overline dark>Análise personalizada</Overline>
              <h2 className="font-serif-ag text-white text-4xl md:text-5xl lg:text-[3.4rem] font-light leading-[1.05] tracking-[-0.015em]">
                Vamos analisar sua operação{" "}
                <span className="italic text-[#C8A25B]">juntos?</span>
              </h2>
              <p className="mt-5 text-white/70 text-base md:text-lg font-light max-w-2xl mx-auto">
                Preencha alguns dados. Em poucos minutos, um consultor sênior
                entrará em contato — sem compromisso.
              </p>
            </div>

            <form
              onSubmit={onSubmit}
              data-testid="nexo-form"
              className="bg-white/5 border border-[#C8A25B]/25 p-8 md:p-10 backdrop-blur-sm space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  label="Seu nome completo"
                  testid="nexo-input-nome"
                  value={nome}
                  onChange={(v) => setNome(v)}
                  placeholder="Como devemos te chamar?"
                />
                <Field
                  label="WhatsApp"
                  testid="nexo-input-wa"
                  value={wa}
                  onChange={handleWa}
                  isRaw
                  placeholder="(12) 99654-7030"
                />
                <Field
                  label="Valor da carta"
                  testid="nexo-input-valor"
                  value={valorStr}
                  onChange={handleValor}
                  isRaw
                  placeholder="R$ 350.000"
                  inputMode="numeric"
                />
                <SelectField
                  label="Administradora"
                  testid="nexo-input-admin"
                  value={admin}
                  onChange={setAdmin}
                  options={ADMINS}
                />
              </div>

              {error && (
                <div
                  data-testid="nexo-form-error"
                  className="text-[#F0CC59] text-sm border-l-2 border-[#F0CC59] pl-4"
                >
                  {error}
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                <p className="text-[11px] text-white/50 leading-relaxed max-w-md">
                  Ao enviar, você concorda em ser contatado pela NEXO. Seus
                  dados são tratados com sigilo e nunca compartilhados com
                  terceiros.
                </p>
                <button
                  type="submit"
                  data-testid="nexo-form-submit"
                  className="group inline-flex items-center gap-3 bg-[#C8A25B] text-[#0D1B2A] px-8 py-4 text-xs tracking-[0.22em] uppercase font-semibold hover:bg-[#D9B672] transition-colors"
                >
                  Enviar análise
                  <ArrowUpRight
                    size={15}
                    strokeWidth={1.8}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </button>
              </div>
            </form>
          </>
        ) : (
          <div
            data-testid="nexo-form-success"
            className="text-center bg-white/5 border border-[#C8A25B]/40 p-10 md:p-16 backdrop-blur-sm"
          >
            <div className="w-16 h-16 mx-auto border border-[#C8A25B]/60 flex items-center justify-center mb-8">
              <Check size={26} strokeWidth={1.6} className="text-[#C8A25B]" />
            </div>
            <Overline dark>Recebido com cuidado</Overline>
            <h2 className="font-serif-ag text-white text-3xl md:text-4xl lg:text-[2.8rem] font-light leading-[1.1] tracking-[-0.015em]">
              Recebemos seus dados.{" "}
              <span className="italic text-[#C8A25B]">
                Em breve, um de nossos especialistas entrará em contato.
              </span>
            </h2>
            <p className="mt-6 text-white/70 text-base md:text-lg font-light max-w-xl mx-auto">
              Se preferir agilizar, fale conosco diretamente pelo WhatsApp
              abaixo — deixamos o canal aberto para você.
            </p>
            <a
              href={nexoWa(
                `Olá NEXO, acabei de enviar meu cadastro. Meu nome é ${nome}.`
              )}
              target="_blank"
              rel="noreferrer"
              data-testid="nexo-form-success-wa"
              className="mt-10 inline-flex items-center gap-3 bg-[#C8A25B] text-[#0D1B2A] px-8 py-4 text-xs tracking-[0.22em] uppercase font-semibold hover:bg-[#D9B672] transition-colors"
            >
              <MessageCircle size={16} strokeWidth={1.8} />
              Falar pelo WhatsApp {NEXO.whatsappDisplay}
              <ArrowUpRight size={15} strokeWidth={1.8} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

function Field({ label, testid, value, onChange, placeholder, isRaw = false, inputMode }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.22em] uppercase text-white/70">
        {label}
      </span>
      <input
        type="text"
        inputMode={inputMode}
        data-testid={testid}
        value={value}
        placeholder={placeholder}
        onChange={isRaw ? onChange : (e) => onChange(e.target.value)}
        className="mt-3 w-full bg-transparent border-b border-[#C8A25B]/30 focus:border-[#C8A25B] outline-none py-3 text-white text-base font-light placeholder:text-white/25"
      />
    </label>
  );
}
function SelectField({ label, testid, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.22em] uppercase text-white/70">
        {label}
      </span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-3 w-full bg-transparent border-b border-[#C8A25B]/30 focus:border-[#C8A25B] outline-none py-3 text-white text-base font-light appearance-none"
        style={{ colorScheme: "dark" }}
      >
        <option value="" style={{ background: "#0D1B2A" }}>
          Selecione...
        </option>
        {options.map((o) => (
          <option key={o} value={o} style={{ background: "#0D1B2A" }}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

// ------------------ FOOTER DISCLAIMER ------------------
function LegalFooter() {
  return (
    <footer
      data-testid="nexo-legal-footer"
      className="bg-[#F5F1EA] border-t border-[#0D1B2A]/10 py-14 md:py-16"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
          <div>
            <div
              className="font-serif-ag text-[#0D1B2A] tracking-[0.42em] font-semibold text-sm"
              data-testid="nexo-footer-brand"
            >
              NEXO
            </div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#7A6033] mt-1">
              Operação de Crédito & Patrimônio
            </div>
            <div className="mt-5 text-[#43536A] text-xs italic max-w-md leading-relaxed">
              “Onde o crédito encontra a oportunidade.”
            </div>
          </div>
          <div className="max-w-lg">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#7A6033] mb-3">
              Aviso legal
            </div>
            <p className="text-[11px] md:text-xs text-[#43536A] leading-relaxed">
              Todas as operações NEXO estão sujeitas à análise individual de
              crédito, viabilidade documental e aprovação da administradora do
              consórcio envolvida. As condições apresentadas podem variar
              conforme perfil do proponente, valor e prazo da carta contemplada.
              A NEXO atua como consultoria especializada em garantia
              imobiliária para consórcios contemplados e não figura como parte
              contratual do consórcio.
            </p>
          </div>
        </div>

        <hr className="border-[#0D1B2A]/10 my-8" />

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[11px] text-[#43536A]">
          <div>
            © {new Date().getFullYear()} NEXO · Todos os direitos reservados.
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/"
              data-testid="nexo-back-ashir"
              className="text-[#7A6033] hover:text-[#C8A25B] tracking-[0.12em] uppercase"
            >
              ← ASHIR GROUP
            </Link>
            <Link
              to="/privacidade"
              data-testid="nexo-privacy"
              className="text-[#7A6033] hover:text-[#C8A25B] tracking-[0.12em] uppercase"
            >
              Política de Privacidade
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ------------------ NEXO FLOATING WHATSAPP ------------------
function NexoFloatWa() {
  return (
    <a
      href={nexoWa()}
      target="_blank"
      rel="noreferrer"
      data-testid="nexo-float-wa"
      aria-label="Falar no WhatsApp NEXO"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg hover:bg-[#1ebd5a] transition-colors ag-pulse"
    >
      <MessageCircle size={26} strokeWidth={1.6} className="text-white" />
    </a>
  );
}

// ------------------ MAIN PAGE ------------------
export default function NexoPage() {
  useEffect(() => {
    document.title =
      "NEXO | Garantia Imobiliária para Cartas Contempladas — ASHIR GROUP";
    const desc = document.querySelector('meta[name="description"]');
    const content =
      "NEXO — Operação de Crédito & Patrimônio. Alternativa de garantia imobiliária para quem tem carta contemplada e precisa liberar o crédito. Análise individual, confidencial e sob medida.";
    if (desc) desc.setAttribute("content", content);
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="nexo-page">
      {/* Slim NEXO header */}
      <header
        data-testid="nexo-header"
        className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[#0D1B2A]/85 border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nexo-header-back"
            className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-white/70 hover:text-[#C8A25B] transition-colors"
          >
            <ArrowLeft size={13} strokeWidth={1.5} />
            ASHIR GROUP
          </Link>
          <div className="flex items-center gap-3">
            <span
              className="font-serif-ag text-[#C8A25B] text-base tracking-[0.42em] font-semibold"
            >
              NEXO
            </span>
            <span className="hidden md:inline w-px h-4 bg-white/20" />
            <span className="hidden md:inline text-[10px] tracking-[0.24em] uppercase text-white/60">
              Crédito & Patrimônio
            </span>
          </div>
          <a
            href="#formulario"
            data-testid="nexo-header-cta"
            className="text-[10px] tracking-[0.22em] uppercase text-[#C8A25B] border-b border-[#C8A25B]/40 pb-0.5 hover:border-[#C8A25B]"
          >
            Análise
          </a>
        </div>
      </header>

      <main className="pt-16">
        <Hero />
        <Pain />
        <Message />
        <Solution />
        <Steps />
        <ForWhom />
        <Credibility />
        <FormSection />
      </main>
      <LegalFooter />
      <NexoFloatWa />
      <CookieConsent />
    </div>
  );
}
